import Model, { attr, hasMany, belongsTo } from '@ember-data/model';
import { type IExtraPriceSchema } from 'uplisting-frontend/models/extra-price';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export interface IQuoteToPrice {
  numberOfGuests: number;
  accommodation: number;
  averagePricePerNight: number;
  balance: number;
  cancellationPayout: number;
  cleaningFee: number;
  commission: number;
  currency: string;
  extraGuest: number;
  extraGuestCharge: number;
  grossRevenue: number;
  guestPrice: number;
  hostEarnings: number;
  hostEdited: boolean;
  managementFee: number;
  netRevenue: number;
  nightlyTax: number;
  personTax: number;
  rentTax: number;
  reservationTax: number;
  subtotal: number;
  visitorLevy: number;
  visitorLevyCollectLocally: boolean;
}

export default class QuoteModel extends Model {
  @attr('day') checkIn!: Date;
  @attr('day') checkOut!: Date;
  @attr('number') numberOfGuests!: number;
  @attr('number') accommodation!: number;
  @attr('number') averagePricePerNight!: number;
  @attr('number') balance!: number;
  @attr('number') cancellationPayout!: number;
  @attr('number') cleaningFee!: number;
  @attr('number') commission!: number;
  @attr('string') currency!: string;
  @attr('number') extraGuest!: number;
  @attr('number') extraGuestCharge!: number;
  @attr('number') grossRevenue!: number;
  @attr('number') guestPrice!: number;
  @attr('number') hostEarnings!: number;
  @attr('boolean') hostEdited!: boolean;
  @attr('number') managementFee!: number;
  @attr('number') netRevenue!: number;
  @attr('number') nightlyTax!: number;
  @attr('number') personTax!: number;
  @attr('number') rentTax!: number;
  @attr('number') reservationTax!: number;
  @attr('number') subtotal!: number;
  @attr('number') visitorLevy!: number;
  @attr('boolean') visitorLevyCollectLocally!: boolean;
  @attr('array') discounts!: IExtraPriceSchema[];
  @attr('array') charges!: IExtraPriceSchema[];

  @belongsTo('booking', { async: false, inverse: null })
  booking!: ModelRegistry['booking'];

  @belongsTo('property', { async: false, inverse: null })
  property!: ModelRegistry['property'];

  @hasMany('extra-price', { async: false, inverse: null })
  fees!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  taxes!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  channelFees!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  cancellationDetails!: ModelRegistry['extra-price'][];

  public toObject(): IQuoteToPrice {
    return {
      accommodation: this.accommodation,
      averagePricePerNight: this.averagePricePerNight,
      balance: this.balance,
      cancellationPayout: this.cancellationPayout,
      cleaningFee: this.cleaningFee,
      commission: this.commission,
      currency: this.currency,
      extraGuest: this.extraGuest,
      extraGuestCharge: this.extraGuestCharge,
      grossRevenue: this.grossRevenue,
      guestPrice: this.guestPrice,
      hostEarnings: this.hostEarnings,
      hostEdited: this.hostEdited,
      managementFee: this.managementFee,
      netRevenue: this.netRevenue,
      nightlyTax: this.nightlyTax,
      numberOfGuests: this.numberOfGuests,
      personTax: this.personTax,
      rentTax: this.rentTax,
      reservationTax: this.reservationTax,
      subtotal: this.subtotal,
      visitorLevy: this.visitorLevy,
      visitorLevyCollectLocally: this.visitorLevyCollectLocally,
    };
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    quote: QuoteModel;
  }
}
